/**
 * useDarkMode
 * ===========
 * Reactive dark-mode signal that works *before* ThemeContext is available
 * (e.g. in AppLoadingScreen, error boundaries, splash screens).
 *
 * Tracks two sources of truth simultaneously:
 *  1. The `dark` class on `document.documentElement` — set by ThemeContext /
 *     useUserAppearance when an explicit preference is stored.
 *  2. The `prefers-color-scheme: dark` media query — the OS-level fallback.
 *
 * Whichever is active at any moment wins via the same logic that `isDarkMode()`
 * used to evaluate once: if `<html>` carries the `dark` class → dark; else
 * follow the media query.
 *
 * The hook is safe to call outside a ThemeProvider and on SSR (returns false).
 */

import { useEffect, useState } from "react";

function readDarkMode(): boolean {
  if (typeof document === "undefined") return false;
  if (document.documentElement.classList.contains("dark")) return true;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return false;
}

export function useDarkMode(): boolean {
  const [dark, setDark] = useState<boolean>(readDarkMode);

  useEffect(() => {
    // Task #3408 Step 16: removed the redundant `setDark(readDarkMode())` call
    // that was here. useState(readDarkMode) already reads the correct initial
    // state synchronously, so calling setDark again in useEffect caused a
    // needless re-render and a brief colour flash on every mount.

    const mq = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;

    const onMediaChange = () => setDark(readDarkMode());

    if (mq) {
      mq.addEventListener("change", onMediaChange);
    }

    const observer = new MutationObserver(() => setDark(readDarkMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      if (mq) mq.removeEventListener("change", onMediaChange);
      observer.disconnect();
    };
  }, []);

  return dark;
}
